import { useCallback, useMemo } from 'react';

import { StoreItem, useShowConsumerStoreItem } from '@givebacks/api/store';
import { useFormListValidation, useFormListValidator } from 'core/Hooks';
import {
  AdditionalFieldsFormsGuard,
  AdditionalFormFieldsForm,
  Modal,
  ModalProps,
  Stack,
  Text,
  createModalsFactory,
} from 'ui';
import { UseFormReturnType, useForm } from 'ui/Form';

import {
  CheckoutButtons,
  CheckoutButtonsProps,
} from '../../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons';
import {
  CheckoutCartOnOptions,
  SharedCheckoutButtonsProps,
} from '../../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons.types';
import classes from './StoreItemModals.module.css';

type SelectStoreItemModalIds = 'StoreItem' | 'AdditionalFields' | 'Child';

/**
 * Props to override the button props for the checkout buttons
 */
type ButtonPropsOverride =
  | CheckoutButtonsProps['addToCartProps']
  | CheckoutButtonsProps['buyNowProps'];

interface SelectStoreItemModalFlowMeta
  extends Pick<SharedCheckoutButtonsProps, 'cartFields' | 'quantity' | 'cartItems'> {
  storeId?: string;
  storeItemId?: string;
  checkoutCartAction?: 'add' | 'buy';
  childModalTitle?: string;
}

const {
  Provider: StoreItemModalFlowProvider,
  useProvider: useStoreItemModalFlowProvider,
  ModalTitleWithBackButton,
} = createModalsFactory<SelectStoreItemModalIds[], SelectStoreItemModalFlowMeta>([
  'StoreItem',
  'AdditionalFields',
  'Child',
]);

type StoreItemAdditionalFieldsFormProps = {
  quantity: number;
  options: StoreItem['options'];
  fieldLabel?: React.ReactNode;
  fieldDescription?: React.ReactNode;
  values?: Record<string, string>;
};

const StoreItemAdditionalFieldsForm = ({
  quantity,
  options,
  fieldLabel,
  fieldDescription,
  values,
}: StoreItemAdditionalFieldsFormProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment -- Array spread is safe here
  const quantityList = useMemo(() => [...Array(quantity).fill([])], [quantity]);

  const form = useForm({
    initialValues: {
      options: quantityList,
    },
    validate:
      {} /** Needed to cause initial form instantiation without any onChanges being triggered.  */,
  });

  // eslint-disable-next-line @typescript-eslint/no-deprecated -- Will be updated in future PR
  useFormListValidator({ form, id: `storeItem_options` });

  return (
    <Stack gap="lg" flex={1}>
      {form.values.options.map((number, idx) => {
        return (
          <Stack key={idx} className={classes.StoreItemAdditionalFieldsForm}>
            <Stack gap="xs">
              <Text fz="14px" fw="600" c="gray.8">
                {fieldLabel} {idx + 1} Info
              </Text>
              <Text fz="12px" c="gray.6">
                {fieldDescription}
              </Text>
            </Stack>
            <AdditionalFieldsFormsGuard
              formId={`storeItem_options.${idx}`}
              options={options}
              onChange={(index, changes) => form.setFieldValue(`options.${idx}`, changes.asObject)}
              values={values}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export interface StoreItemAdditionalFieldsCustomFooterProps {
  onClose?: () => void;
  cartFields: SharedCheckoutButtonsProps['cartFields'];
  cartItems: SharedCheckoutButtonsProps['cartItems'];
  quantity?: number;
}

interface StoreItemAdditionalFieldsFooterProps
  extends Pick<
    SharedCheckoutButtonsProps,
    'cartFields' | 'storeId' | 'storeItemId' | 'quantity' | 'cartItems'
  > {
  onClose?: () => void;
  isAddToCart?: boolean;
  buttonPropsOverride?: ButtonPropsOverride;
  customFooter?: (props: StoreItemAdditionalFieldsCustomFooterProps) => React.ReactNode;
}

type StoreItemOrderForms = {
  storeItem_options?: UseFormReturnType<AdditionalFormFieldsForm>;
};

const StoreItemAdditionalFieldsFooter = ({
  customFooter,
  quantity,
  storeId,
  storeItemId,
  cartItems,
  onClose,
  isAddToCart,
  cartFields,
  buttonPropsOverride = {},
}: StoreItemAdditionalFieldsFooterProps) => {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- Will be updated in future PR
  const { useGetForms } = useFormListValidation<StoreItemOrderForms>();
  const forms = useGetForms();

  const cartItem = useMemo(() => {
    const firstCartItem = cartItems?.[0];

    if (!firstCartItem) {
      return {};
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Omit quantity store_item_id as they are provided via props
    const { quantity: _quantity, store_item_id, ...cartItem } = firstCartItem;
    return cartItem;
  }, [cartItems]);

  /** Returns an array of cart items based on the selected quantity and options **/
  const _cartItems = useMemo(
    () =>
      forms.storeItem_options?.values?.options?.map((options) => ({
        options: options,
        store_item_id: storeItemId,
        ...cartItem,
      })),

    [cartItem, forms.storeItem_options?.values?.options, storeItemId]
  );

  const buttonProps = useMemo(
    () => ({
      validateOnClick: true,
      buttonProps: { children: 'Continue', variant: 'filled' },
      ...buttonPropsOverride,
    }),
    [buttonPropsOverride]
  );

  if (customFooter) {
    return customFooter({
      onClose,
      quantity,
      cartItems: _cartItems,
      cartFields,
    });
  }

  return (
    <CheckoutButtons
      storeId={storeId}
      storeItemId={storeItemId}
      quantity={quantity}
      onCartCreate={() => onClose?.()}
      addToCartVisible={isAddToCart}
      buyNowVisible={!isAddToCart}
      cartItems={_cartItems}
      buyNowProps={buttonProps}
      addToCartProps={buttonProps}
      cartFields={cartFields}
    />
  );
};

const StoreItemDataProvider = ({
  storeId,
  storeItemId,
  children,
}: {
  storeId?: string;
  storeItemId?: string;
  children: ({ storeItem }: { storeItem: StoreItem | undefined }) => React.ReactNode;
}) => {
  const { data: storeItem } = useShowConsumerStoreItem({ causeId: storeId, uuid: storeItemId });

  return children({ storeItem });
};

interface StoreItemOptionsModalProps extends Omit<ModalProps, 'opened' | 'onClose' | 'title'> {
  customTitle?: string;
  customFooter?: React.FunctionComponent<{
    onClose?: () => void;
    cartFields: SharedCheckoutButtonsProps['cartFields'];
    cartItems: SharedCheckoutButtonsProps['cartItems'];
    quantity?: number;
  }>;
  onBuyNow?: () => void;
  fieldLabel?: React.ReactNode;
  fieldDescription?: React.ReactNode;
  buttonPropsOverride?: ButtonPropsOverride;
  values?: Record<string, string>;
}

/**
 * Creates a form with additional field for the store item
 * This should only be used with one store item at a time
 */
const StoreItemAdditionalFieldsModal = ({
  customTitle,
  customFooter,
  fieldLabel,
  fieldDescription,
  buttonPropsOverride,
  values,
  ...rest
}: StoreItemOptionsModalProps) => {
  const { opened, close, meta } = useStoreItemModalFlowProvider({
    id: 'AdditionalFields',
  });

  const { quantity = 1, checkoutCartAction, cartFields, cartItems } = meta;

  const title = useMemo(() => {
    if (customTitle) {
      return customTitle;
    }
    return checkoutCartAction == 'add' ? 'Add To Cart' : 'Buy Now';
  }, [customTitle, checkoutCartAction]);

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title}</ModalTitleWithBackButton>}
      {...rest}
    >
      <StoreItemDataProvider storeId={meta.storeId} storeItemId={meta.storeItemId}>
        {({ storeItem }) => (
          <>
            <StoreItemAdditionalFieldsForm
              quantity={quantity}
              options={storeItem?.options}
              fieldLabel={fieldLabel}
              fieldDescription={fieldDescription}
              values={values}
            />
            {meta.storeId && meta.storeItemId && (
              <StoreItemAdditionalFieldsFooter
                customFooter={customFooter}
                storeId={meta.storeId}
                storeItemId={meta.storeItemId}
                onClose={close}
                isAddToCart={checkoutCartAction == 'add'}
                cartFields={cartFields}
                cartItems={cartItems}
                buttonPropsOverride={buttonPropsOverride}
              />
            )}
          </>
        )}
      </StoreItemDataProvider>
    </Modal>
  );
};

export interface StoreItemModalProps {
  opened: boolean;
  onClose: () => void;
  openModalById: (id: SelectStoreItemModalIds) => void;
  meta?: SelectStoreItemModalFlowMeta;
  openAdditionalFields: CheckoutCartOnOptions;
  setMeta: (meta: SelectStoreItemModalFlowMeta) => void;
}

const StoreItemModalWrapper = ({
  modal,
  modalProps,
}: {
  modal?: (props: StoreItemModalProps) => React.ReactNode | React.ReactNode[];
  modalProps?: Partial<Omit<ModalProps, 'opened' | 'onClose' | 'title'>>;
}) => {
  const { opened, close, openModalById, setMeta } = useStoreItemModalFlowProvider({
    id: 'StoreItem',
  });

  const handleOptionalFields: CheckoutCartOnOptions = useCallback(
    ({ quantity, checkoutCartAction, cartFields, cartItems }) => {
      setMeta((prev) => ({ ...prev, quantity, checkoutCartAction, cartFields, cartItems }));
      openModalById('AdditionalFields');
    },
    [openModalById, setMeta]
  );

  return modal?.({
    opened,
    onClose: close,
    openModalById,
    setMeta,
    openAdditionalFields: handleOptionalFields,
    ...modalProps,
  });
};

interface StoreItemChildModalWrapperProps {
  opened: boolean;
  onClose: () => void;
  openModalById: (id: SelectStoreItemModalIds) => void;
  meta?: ReturnType<typeof useStoreItemModalFlowProvider>['meta'];
  setMeta?: ReturnType<typeof useStoreItemModalFlowProvider>['setMeta'];
  currentModalId?: ReturnType<typeof useStoreItemModalFlowProvider>['currentModalId'];
  ModalTitleWithBackButton?: typeof ModalTitleWithBackButton;
}

/**
 *
 * Provides a child function with access to the store item modal flow and meta data
 */
const StoreItemChildModalWrapper = ({
  modal,
}: {
  modal?: (props: StoreItemChildModalWrapperProps) => React.ReactNode | React.ReactNode[];
}) => {
  const { opened, close, openModalById, meta, setMeta, currentModalId } =
    useStoreItemModalFlowProvider({
      id: 'Child',
    });

  return modal?.({
    opened,
    onClose: close,
    openModalById,
    meta,
    setMeta,
    currentModalId,
    ModalTitleWithBackButton,
  });
};

export type StoreItemModalsProps = {
  storeId?: string;
  storeItemId?: string;
  children?: (props: StoreItemModalProps) => React.ReactNode | React.ReactNode[];
  /** Modal in which you wish to pass as a child to the provider */
  childModal?: (arg0: StoreItemChildModalWrapperProps) => React.ReactNode | React.ReactNode[];
  additionalFieldsProps?: {
    fieldLabel?: React.ReactNode;
    fieldDescription?: React.ReactNode;
    customTitle?: string;
    customFooter?: StoreItemAdditionalFieldsFooterProps['customFooter'];
    /**
     * Props to override the button props for the checkout buttons
     */
    buttonPropsOverride?: ButtonPropsOverride;
    values?: Record<string, string>;
  };
  modalProps?: Partial<Omit<ModalProps, 'opened' | 'onClose' | 'title'>>;
};

/**
 *
 * Used to provide a store Item with a dynamic modal flow where
 * the user can select additional fields before adding to cart
 * if the store item requires additional fields
 *
 *  @important This component must only be used with one store item at a time, where cart_items contains only a single index
 *
 */
const StoreItemModals = ({
  children,
  storeId,
  storeItemId,
  additionalFieldsProps,
  modalProps,
  childModal,
}: StoreItemModalsProps) => {
  return (
    <StoreItemModalFlowProvider
      meta={{
        storeId,
        storeItemId,
      }}
    >
      {childModal ? <StoreItemChildModalWrapper modal={childModal} /> : null}
      <StoreItemModalWrapper modal={children} modalProps={modalProps} />
      <StoreItemAdditionalFieldsModal {...additionalFieldsProps} {...modalProps} />
    </StoreItemModalFlowProvider>
  );
};

export { StoreItemModals };
